import { Box, IconButton, Button, Input, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Chip } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';
import CategoryIcon from '@mui/icons-material/Category';
import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../../assets/errors/nodata.png'; // Adjust the path as necessary

const Gallery = () => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();

  // Category enum options
  const categoryOptions = [
    'steam',
    'robotics',
    'coding',
    'competition',
    'event',
    'students',
    'workshop',
    'general',
    'bootcamp',
    'expo',
    'training'
  ];

  // Form state with category field
  const [formData, setFormData] = useState({
    image_name: '',
    category: 'general', // Default to 'general'
    image: '',
    video: '',
    date: ''
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getData('proccess.php', 'gallery'); // Fetch gallery data
        if (response.success) {
          setGallery(Array.isArray(response.data) ? response.data : []);
        } else {
          toast.error(response.message || "Failed to load gallery data");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch gallery");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  const handleAddClick = () => {
    setFormData({
      image_name: '',
      category: 'general', // Default to 'general'
      image: '',
      video: '',
      date: new Date().toISOString().split('T')[0] // Set today's date as default
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (imageItem) => {
    setSelectedImage(imageItem);
    setFormData({
      image_name: imageItem.image_name || '',
      category: imageItem.category || 'general',
      image: imageItem.image || '',
      video: imageItem.video || '',
      date: imageItem.date ? imageItem.date.split(' ')[0] : new Date().toISOString().split('T')[0]
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (imageItem) => {
    setImageToDelete(imageItem);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData('proccess.php', 'gallery', imageToDelete.id); // Delete image by ID
      if (response.success) {
        toast.success(`Image deleted successfully`);
        setGallery(gallery.filter(item => item.id !== imageToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete image");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete image");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setImageToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && !['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
      toast.error("Please upload a valid image (JPEG, PNG, or GIF)");
      return;
    }
    setFormData(prev => ({
      ...prev,
      image: file
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare form data for submission
    const submitData = new FormData();
    submitData.append('image_name', formData.image_name);
    submitData.append('category', formData.category);
    submitData.append('video', formData.video);
    submitData.append('date', formData.date);
    
    // Handle image
    if (formData.image instanceof File) {
      submitData.append('image', formData.image);
    } else if (formData.image && typeof formData.image === 'string') {
      submitData.append('existing_image', formData.image);
    }

    try {
      let response;

      if (selectedImage) {
        // Update existing image
        response = await updateData('proccess.php', 'gallery', formData, selectedImage.id);
        if (response.success) {
          toast.success(`Image updated successfully`);
          setGallery(response.data || gallery.map(item => 
            item.id === selectedImage.id 
              ? { 
                  ...item, 
                  ...formData, 
                  image: formData.image instanceof File ? formData.image.name : (formData.image || item.image),
                  category: formData.category
                } 
              : item
          ));
        }
      } else {
        // Add new image
        response = await addData('proccess.php', 'gallery', formData);
        if (response.success) {
          toast.success(`Image added successfully`);
          if (Array.isArray(response.data)) {
            setGallery(response.data);
          } else {
            setGallery(prev => [...prev, response.data]);
          }
        }
      }

      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedImage(null);
  };

  // Function to get color for category chip
  const getCategoryColor = (category) => {
    const categoryColors = {
      'steam': { bg: '#4a90e2', color: 'white' },
      'robotics': { bg: '#50c878', color: 'white' },
      'coding': { bg: '#6a5acd', color: 'white' },
      'competition': { bg: '#ff6b6b', color: 'white' },
      'event': { bg: '#ffa500', color: 'white' },
      'students': { bg: '#ff69b4', color: 'white' },
      'workshop': { bg: '#20b2aa', color: 'white' },
      'general': { bg: '#808080', color: 'white' },
      'bootcamp': { bg: '#8b4513', color: 'white' },
      'expo': { bg: '#9370db', color: 'white' },
      'training': { bg: '#2e8b57', color: 'white' }
    };
    
    return categoryColors[category] || { bg: colors.primary[400], color: 'white' };
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 20 },
    { field: "image_name", headerName: "Image Name", flex: 1, minWidth: 120 },
    { 
      field: "category", 
      headerName: "Category", 
      flex: 0.8, 
      minWidth: 120,
      renderCell: (params) => {
        const categoryStyle = getCategoryColor(params.value);
        return (
          <Chip
            label={params.value ? params.value.charAt(0).toUpperCase() + params.value.slice(1) : 'General'}
            size="small"
            sx={{
              backgroundColor: categoryStyle.bg,
              color: categoryStyle.color,
              fontWeight: '500',
              textTransform: 'capitalize'
            }}
          />
        );
      }
    },
    {
      field: "image",
      headerName: "Image",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box width={50} height={50}>
          {params.value ? (
            <img
              src={`https://mindsinaction.com.na/assets/media/pictures/${params.value}`}
              alt={params.value || "Gallery Image"}
              style={{ width: 50, height: 50, objectFit: "cover", cursor: "pointer" }}
              onClick={() => window.open(`https://mindsinaction.com.na/assets/media/pictures/${params.value}`, "_blank")}
            />
          ) : (
            "No image"
          )}
        </Box>
      )
    },
    {
      field: "video",
      headerName: "Video URL",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        params.value ? (
          <a
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: colors.blue[400], textDecoration: 'underline' }}
          >
            View Video
          </a>
        ) : (
          <Typography sx={{ color: colors.grey ? colors.grey[500] : colors.primary[200] }}>
            No video
          </Typography>
        )
      )
    },
    { 
      field: "date", 
      headerName: "Date Added", 
      flex: 0.8, 
      minWidth: 100,
      renderCell: (params) => (
        <Typography>
          {params.value ? new Date(params.value).toLocaleDateString() : ''}
        </Typography>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap="8px">
          <IconButton
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.orange[400] }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No images available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Add/Edit Dialog */}
      <Dialog
        open={openEditDialog || openAddDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedImage(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedImage ? "Edit Image" : "Add New Image"}</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{ width: "100%", padding: "1rem" }}>
            <Box display={"flex"} gap={"1rem"} width={"100%"} mb={"1rem"} flexWrap="wrap">
              <TextField
                style={{ width: "100%" }}
                id="image_name"
                name="image_name"
                label="Image Name"
                variant="standard"
                value={formData.image_name}
                onChange={handleFormChange}
                required
              />
              
              {/* Category Select Field */}
              <FormControl variant="standard" style={{ width: "100%", marginTop: "0.5rem" }}>
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleFormChange}
                  label="Category"
                  required
                  startAdornment={
                    <CategoryIcon sx={{ color: colors.primary[200], mr: 1 }} />
                  }
                >
                  {categoryOptions.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Box width={"100%"} sx={{ mt: 2 }}>
                <label>Image File</label>
                <Input
                  type="file"
                  style={{ width: "100%", marginTop: "0.5rem" }}
                  id="image"
                  name="image"
                  onChange={handleFileChange}
                  inputProps={{ accept: "image/jpeg,image/png,image/gif" }}
                  required={!selectedImage}
                />
                {formData.image && typeof formData.image === 'string' && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Current image: {formData.image}
                  </Typography>
                )}
              </Box>
              
              <TextField
                style={{ width: "100%" }}
                id="video"
                name="video"
                label="Video Link"
                variant="standard"
                value={formData.video}
                onChange={handleFormChange}
                placeholder="https://www.youtube.com/watch?v=..."
                helperText="Optional: Link to YouTube or Vimeo video"
              />
              
              <TextField
                style={{ width: "100%" }}
                id="date"
                name="date"
                label="Date Added"
                variant="standard"
                type="date"
                value={formData.date}
                onChange={handleFormChange}
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setOpenAddDialog(false);
              setSelectedImage(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{ backgroundColor: colors.blue[400] }}
            variant="contained"
            endIcon={<SendIcon />}
          >
            {selectedImage ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{imageToDelete?.image_name}"?
          </Typography>
          {imageToDelete?.category && (
            <Typography variant="caption" display="block" sx={{ mt: 1, color: colors.grey ? colors.grey[600] : colors.primary[300] }}>
              Category: {imageToDelete.category}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{ backgroundColor: colors.red[400] }}
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mb={"30px"} ml={"15px"} mr={"15px"} display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
        <Box flex={1}>
          <Typography
            variant="h4"
            color={colors.dark[400]}
            fontWeight={"bold"}
            sx={{ mb: "5px" }}
          >
            Gallery Management
          </Typography>
          <Typography variant="h5" color={colors.blue[400]}>
            Manage gallery images
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{
            backgroundColor: colors.blue[400],
            '&:hover': {
              backgroundColor: colors.blue[500],
            }
          }}
        >
          Add Image
        </Button>
      </Box>
      <Divider component="" />
      <Box sx={{ height: "45vh", width: "100%" }} backgroundColor={colors.dark[300]} padding={".2rem"} mt={"1rem"}>
        <DataGrid
          rows={gallery}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          sx={{
            border: "none",
            ".MuiDataGrid-cell": {
              borderBottom: "none",
              backgroundColor: "none"
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: colors.primary[900],
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: colors.primary[900],
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search...",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Gallery;